import React, { useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { useCreches } from '../hooks/useCreches';
import { getCurrentTenantId } from '../services/api';
import { formatarDataBR } from '../common/data';
import PainelInfraestrutura, { LABEL_ESTADO_PATRIMONIO } from './PainelInfraestrutura';
import ChamadosSection from './ChamadosSection';

/**
 * Infraestrutura — o que a landing page vende no card de plano Ouro:
 * "chamados de manutenção, vistorias prediais e gestão de patrimônio".
 *
 * Das três, vistoria já existia (dentro do Administrativo) e patrimônio existia
 * como um inventário que sabia quantos berços havia e não sabia se algum estava
 * quebrado. Chamado de manutenção não existia de forma alguma.
 *
 * A tela é montada na ordem em que o gestor decide: primeiro o que exige ação
 * hoje, depois a fila de trabalho, e por último o cadastro de bens — que é
 * consulta, não decisão.
 */

interface BemPatrimonio {
  id: number;
  id_creche: number;
  item: string;
  categoria: string;
  quantidade: number;
  validade: string;
  numero_patrimonio: string | null;
  local: string | null;
  responsavel: string | null;
  estado: string;
}

const ESTADOS = ['em_uso', 'em_manutencao', 'inutilizado', 'descartado'];

const COR_ESTADO: Record<string, string> = {
  em_uso: '#2e7d32', em_manutencao: '#e65100', inutilizado: '#c62828', descartado: '#616161',
};

export default function InfraestruturaPage() {
  const { options } = useCreches();
  // Com tenant ativo o backend força `id_creche` pelo token; sem ele (admin de
  // sistema sem creche escolhida) o seletor precisa aparecer. Mesmo critério da
  // AdministrativoPage — a coluna real é `id_creche`, nunca `creche_id`.
  const tenantScoped = Number(getCurrentTenantId() || 0) > 0;
  const classeCampoCreche = tenantScoped ? 'campo-oculto' : '';

  const [recarregar, setRecarregar] = useState(0);
  const [chamadoParaAbrir, setChamadoParaAbrir] = useState<number | null>(null);
  const mudou = () => setRecarregar((v) => v + 1);

  return (
    <div className="pagina">
      <h1>Infraestrutura</h1>
      <p style={{ color: '#666', marginTop: -8 }}>
        Chamados de manutenção, patrimônio da unidade e os apontamentos de vistoria que ainda
        não viraram conserto.
      </p>

      <PainelInfraestrutura
        recarregarEm={recarregar}
        aoAbrirChamado={(id) => setChamadoParaAbrir(id)}
        aoMudar={mudou}
      />

      <ChamadosSection abrirId={chamadoParaAbrir} aoMudar={mudou} />

      <TabelaCrud<BemPatrimonio>
        titulo="Patrimônio da unidade"
        endpoint="/infraestrutura/patrimonio"
        colunas={[
          { chave: 'item', label: 'Bem' },
          { chave: 'numero_patrimonio', label: 'Tombo' },
          { chave: 'local', label: 'Local' },
          { chave: 'quantidade', label: 'Qtd.' },
          {
            chave: 'estado',
            label: 'Estado',
            render: (b) => (
              <span style={{ color: COR_ESTADO[b.estado] || '#616161', fontWeight: 600 }}>
                {LABEL_ESTADO_PATRIMONIO[b.estado] || b.estado}
              </span>
            ),
          },
          { chave: 'validade', label: 'Validade', render: (b) => formatarDataBR(b.validade) || '—' },
        ]}
        itemVazio={{
          id_creche: 0, item: '', categoria: '', quantidade: 1, validade: '',
          numero_patrimonio: '', local: '', responsavel: '', estado: 'em_uso',
        }}
        validateItem={(b) => {
          if (!tenantScoped && (!b.id_creche || Number(b.id_creche) <= 0)) return 'Selecione a Creche.';
          if (!b.item) return 'Informe o bem.';
          return null;
        }}
        renderFormulario={(b, onChange) => (
          <>
            <div className="linha-campos linha-3">
              <label className={classeCampoCreche}>Creche
                <select value={b.id_creche ?? 0} onChange={(e) => onChange('id_creche', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label>Bem<input value={b.item ?? ''} onChange={(e) => onChange('item', e.target.value)} /></label>
              <label>Categoria<input value={b.categoria ?? ''} onChange={(e) => onChange('categoria', e.target.value)} /></label>
            </div>
            <div className="linha-campos linha-3">
              <label>Nº de tombo
                <input value={b.numero_patrimonio ?? ''} onChange={(e) => onChange('numero_patrimonio', e.target.value)} />
              </label>
              <label>Local
                <input value={b.local ?? ''} onChange={(e) => onChange('local', e.target.value)} placeholder="Ex.: Sala do Berçário I" />
              </label>
              <label>Responsável
                <input value={b.responsavel ?? ''} onChange={(e) => onChange('responsavel', e.target.value)} />
              </label>
            </div>
            <div className="linha-campos linha-3">
              <label>Quantidade
                <input type="number" value={b.quantidade ?? 0} onChange={(e) => onChange('quantidade', Number(e.target.value))} />
              </label>
              <label>Validade
                <input type="date" value={b.validade ?? ''} onChange={(e) => onChange('validade', e.target.value)} />
              </label>
              <label>Estado
                <select value={b.estado ?? 'em_uso'} onChange={(e) => onChange('estado', e.target.value)}>
                  {ESTADOS.map((e2) => <option key={e2} value={e2}>{LABEL_ESTADO_PATRIMONIO[e2]}</option>)}
                </select>
              </label>
            </div>
            <p style={{ color: '#777', fontSize: 12, margin: 0 }}>
              O estado muda sozinho quando um chamado de manutenção é aberto ou resolvido contra este
              bem — e a mudança fica registrada no histórico do chamado. Um bem marcado como inutilizado
              ou descartado não volta a “em uso” por causa disso: essa decisão é da creche.
            </p>
          </>
        )}
      />
    </div>
  );
}
